import { useState } from "react";

import {
  PASSKEY_REMOVED,
  PASSKEY_REMOVE_FAILED,
  PASSKEY_RENAMED,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  useDeleteWebauthnCredentialMutation,
  useGetWebauthnCredentialsQuery,
  useRenameWebauthnCredentialMutation,
  WebAuthnCredentialSummary,
} from "store/apis/webauthn";

import { AddPasskeyButton } from "./AddPasskeyButton";
import { PasskeyListItem } from "./PasskeyListItem";
import { PasskeyNameModal } from "./PasskeyNameModal";
import { RemoveWebAuthnCredentialModal } from "./RemoveWebAuthnCredentialModal";

export const ManagePasskeys = () => {
  const [credentialToRemove, setCredentialToRemove] =
    useState<WebAuthnCredentialSummary | null>(null);
  const [credentialToRename, setCredentialToRename] =
    useState<WebAuthnCredentialSummary | null>(null);
  const removeModal = useModal();
  const renameModal = useModal();
  const { alertToast } = useToast();
  const { trackEvent } = useAnalytics();

  const {
    data: credentials,
    isLoading,
    isSuccess,
    isError,
  } = useGetWebauthnCredentialsQuery();
  const [deleteWebauthnCredential, { isLoading: isRemoving }] =
    useDeleteWebauthnCredentialMutation();
  const [renameWebauthnCredential, { isLoading: isRenaming }] =
    useRenameWebauthnCredentialMutation();

  const handleRemoveClick = (credential: WebAuthnCredentialSummary) => {
    setCredentialToRemove(credential);
    removeModal.open();
  };

  const handleRemoveCancel = () => {
    removeModal.close();
  };

  const removeCredential = async () => {
    if (!credentialToRemove) return;
    try {
      await deleteWebauthnCredential({
        uuid: credentialToRemove.uuid,
      }).unwrap();
      trackEvent(PASSKEY_REMOVED);
      removeModal.close();
    } catch (error) {
      trackEvent(PASSKEY_REMOVE_FAILED);
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleRemoveConfirm = () => {
    void removeCredential();
  };

  const handleRenameClick = (credential: WebAuthnCredentialSummary) => {
    setCredentialToRename(credential);
    renameModal.open();
  };

  const handleRenameCancel = () => {
    renameModal.close();
  };

  const renameCredential = async (deviceName: string) => {
    if (!credentialToRename) return;
    try {
      await renameWebauthnCredential({
        uuid: credentialToRename.uuid,
        device_name: deviceName,
      }).unwrap();
      trackEvent(PASSKEY_RENAMED);
      renameModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  const handleRenameSubmit = (deviceName: string) => {
    void renameCredential(deviceName);
  };

  return (
    <div className="t-flex t-flex-col t-gap-1.5">
      <div className="t-flex t-items-center t-justify-between">
        <div className="t-text-body-sm t-text-text-30">Passkeys</div>
        <AddPasskeyButton />
      </div>
      <div className="t-text-body-sm-lite t-text-neutral-50">
        Sign in with your fingerprint, face, or device PIN instead of a
        password. You can also use other sign-in options, such as a magic link
        sent to your email.
      </div>

      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isError={isError}
        isEmpty={credentials?.length === 0}
        loaderType="secondary"
      >
        <Async.Empty>
          <div className="t-text-body-sm t-text-neutral-50 t-py-3">
            No passkeys added yet
          </div>
        </Async.Empty>
        <Async.Success>
          <div className="t-mt-2 t-flex t-flex-col t-rounded-lg t-border t-border-solid t-border-neutral-10 t-px-4">
            {credentials?.map((credential, index) => (
              <PasskeyListItem
                key={credential.uuid}
                credential={credential}
                onRemove={handleRemoveClick}
                onRename={handleRenameClick}
                showTopBorder={index > 0}
              />
            ))}
          </div>
        </Async.Success>
      </Async.Root>

      <RemoveWebAuthnCredentialModal
        open={removeModal.isOpen}
        deviceName={credentialToRemove?.device_name || "this passkey"}
        isRemoving={isRemoving}
        onCancel={handleRemoveCancel}
        onConfirm={handleRemoveConfirm}
      />

      <PasskeyNameModal
        open={renameModal.isOpen}
        title="Rename passkey"
        initialValue={credentialToRename?.device_name || ""}
        isSaving={isRenaming}
        onSubmit={handleRenameSubmit}
        onCancel={handleRenameCancel}
        secondaryAction={{ label: "Cancel", onClick: handleRenameCancel }}
      />
    </div>
  );
};
